import { Check, Lock, Save } from "lucide-react";
import { Link } from "react-router-dom";
import { artAssets, officialTcgCards } from "../data/site";
import { cardById, cardPower, DECK_SIZE, isSlottable, variantOf } from "../utils/tcg";

/* Six-slot battle deck assembly. The six house loaners are always slottable;
   every other unit must be pulled from a supply crate first. Guests save to
   the device; signed-in runners also lock the deck to their account vault. */
export function TcgDeckBuilder({ deck, collection, onToggle, onSave, saving, user }) {
  const full = deck.length === DECK_SIZE;
  const legal = full && deck.every((id) => isSlottable(id, collection));
  const illegalCount = deck.filter((id) => !isSlottable(id, collection)).length;
  const totalPower = deck.reduce((sum, id) => sum + (cardById(id) ? cardPower(cardById(id)) : 0), 0);

  return (
    <div className="deck-builder" data-testid="deck-builder">
      <div className="deck-tray-head">
        <h3>
          YOUR BATTLE DECK <span data-testid="deck-count">{deck.length} / {DECK_SIZE}</span>
        </h3>
        <p>
          Tap cards to slot them. Six unique units, no repeats. Total deck power reads {totalPower}. Dimmed
          units are crate-locked — pull them from the PACK CRATE to wake them.
        </p>
        {illegalCount > 0 && (
          <p className="tray-warning" data-testid="deck-illegal-warning">
            DECK HOLDS {illegalCount} UNPULLED UNIT{illegalCount > 1 ? "S" : ""} — SWAP {illegalCount > 1 ? "THEM" : "IT"} TO ENTER BATTLE.
          </p>
        )}
      </div>

      <div className="deck-tray" data-testid="deck-tray">
        {Array.from({ length: DECK_SIZE }).map((_, index) => {
          const card = deck[index] ? cardById(deck[index]) : null;
          return card ? (
            <button
              type="button"
              key={card.id}
              className={`deck-slot is-filled rarity-${card.rarity}${isSlottable(card.id, collection) ? "" : " is-locked"}`}
              onClick={() => onToggle(card.id)}
              data-testid={`deck-slot-${card.id}`}
              aria-label={`Remove ${card.name} from deck`}
            >
              <img src={card.image} alt={`${card.name} card`} />
              <span>{card.name.split(",")[0]}{variantOf(card) ? ` · ${variantOf(card)}` : ""}</span>
            </button>
          ) : (
            <div key={`empty-${index}`} className="deck-slot is-empty" data-testid={`deck-slot-empty-${index}`}>
              <img src={artAssets.cardBack} alt="" aria-hidden="true" />
              <span>EMPTY SLOT</span>
            </div>
          );
        })}
      </div>

      <div className="deck-save-row">
        <button
          type="button"
          className="deck-save-button"
          disabled={!legal || saving}
          onClick={onSave}
          data-testid="deck-save-button"
        >
          <Save size={15} /> {saving ? "SAVING…" : legal ? "SAVE DECK" : full ? "SWAP UNPULLED UNITS" : `SLOT ${DECK_SIZE - deck.length} MORE`}
        </button>
        {!user && (
          <p className="deck-guest-note" data-testid="deck-guest-note">
            Deck saves to this device. <Link to="/account">Sign in</Link> to lock it to your account across every device.
          </p>
        )}
        {user && <p className="deck-guest-note is-signed">Signed in as {user.name || "RUNNER"} — saves lock to your account vault.</p>}
      </div>

      <div className="deck-builder-grid" data-testid="deck-builder-grid">
        {officialTcgCards.map((card) => {
          const inDeck = deck.includes(card.id);
          const locked = !isSlottable(card.id, collection);
          const owned = collection?.[card.id] || 0;
          return (
            <button
              type="button"
              key={card.id}
              className={`deck-card-tile rarity-${card.rarity}${inDeck ? " in-deck" : ""}${locked ? " is-locked" : ""}`}
              onClick={() => onToggle(card.id)}
              data-testid={`deck-card-${card.id}`}
              aria-pressed={inDeck}
              aria-disabled={locked && !inDeck}
              aria-label={
                locked && !inDeck
                  ? `${card.name} locked — pull from a supply crate to unlock`
                  : `${inDeck ? "Remove" : "Add"} ${card.name} ${inDeck ? "from" : "to"} deck`
              }
            >
              <img loading="lazy" src={card.image} alt={`${card.name} TCG card`} />
              <span className="deck-card-name">{card.name.split(",")[0]}</span>
              {variantOf(card) && <span className="variant-chip deck-card-variant" data-testid={`deck-variant-${card.id}`}>{variantOf(card)} · {card.cardNo}</span>}
              <span className="deck-card-stats">
                S{card.stats.scrap} · A{card.stats.atk} · D{card.stats.def}
              </span>
              <span className="deck-card-power">PWR {cardPower(card)}</span>
              {locked && !inDeck && (
                <span className="lock-chip" data-testid={`deck-lock-${card.id}`}>
                  <Lock size={10} /> PULL FROM CRATES
                </span>
              )}
              {!locked && owned > 1 && <span className="owned-chip">×{owned}</span>}
              {inDeck && (
                <span className="deck-in-badge">
                  <Check size={11} /> SLOTTED
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
